import React, { useState } from 'react';

const TeamManagement = ({ 
  teams, 
  setTeams, 
  tournaments, 
  setTournaments, 
  currentTournament, 
  setCurrentTournament 
}) => {
  const [newTeamName, setNewTeamName] = useState('');
  const [editingTeam, setEditingTeam] = useState(null);
  const [editName, setEditName] = useState('');
  const [newTournamentName, setNewTournamentName] = useState('');
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  
  // 選手管理用の状態
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerRole, setNewPlayerRole] = useState('サバイバー');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editPlayerName, setEditPlayerName] = useState('');
  const [editPlayerRole, setEditPlayerRole] = useState('');

  const addTeam = () => {
    if (newTeamName.trim() === '') return;
    
    const newTeam = {
      id: Date.now(),
      name: newTeamName.trim(),
      createdAt: new Date().toISOString(),
      players: [] // 選手リストを追加
    };
    
    setTeams([...teams, newTeam]);
    setNewTeamName('');
  };

  const deleteTeam = (teamId) => {
    if (window.confirm('このチームを削除しますか？所属選手も削除されます。')) {
      setTeams(teams.filter(team => team.id !== teamId));
      if (selectedTeam && selectedTeam.id === teamId) {
        setSelectedTeam(null);
      }
    }
  };

  const startEdit = (team) => {
    setEditingTeam(team.id);
    setEditName(team.name);
  };

  const saveEdit = () => {
    if (editName.trim() === '') return;
    
    setTeams(teams.map(team => 
      team.id === editingTeam 
        ? { ...team, name: editName.trim() }
        : team
    ));
    setEditingTeam(null);
    setEditName('');
  };

  const cancelEdit = () => {
    setEditingTeam(null);
    setEditName('');
  };

  // 選手管理機能
  const addPlayer = () => {
    if (!selectedTeam || newPlayerName.trim() === '') return;
    
    const newPlayer = {
      id: Date.now(),
      name: newPlayerName.trim(),
      role: newPlayerRole,
      createdAt: new Date().toISOString()
    };
    
    setTeams(teams.map(team => 
      team.id === selectedTeam.id 
        ? { ...team, players: [...team.players, newPlayer] }
        : team
    ));
    
    // selectedTeamも更新
    setSelectedTeam({
      ...selectedTeam,
      players: [...selectedTeam.players, newPlayer]
    });
    
    setNewPlayerName('');
    setNewPlayerRole('サバイバー');
  };

  const deletePlayer = (playerId) => {
    if (!selectedTeam || !window.confirm('この選手を削除しますか？')) return;
    
    const updatedPlayers = selectedTeam.players.filter(player => player.id !== playerId);
    
    setTeams(teams.map(team => 
      team.id === selectedTeam.id 
        ? { ...team, players: updatedPlayers }
        : team
    ));
    
    setSelectedTeam({
      ...selectedTeam,
      players: updatedPlayers
    });
  };

  const startEditPlayer = (player) => {
    setEditingPlayer(player.id);
    setEditPlayerName(player.name);
    setEditPlayerRole(player.role);
  };

  const saveEditPlayer = () => {
    if (!selectedTeam || editPlayerName.trim() === '') return;
    
    const updatedPlayers = selectedTeam.players.map(player => 
      player.id === editingPlayer 
        ? { ...player, name: editPlayerName.trim(), role: editPlayerRole }
        : player
    );
    
    setTeams(teams.map(team => 
      team.id === selectedTeam.id 
        ? { ...team, players: updatedPlayers }
        : team
    ));
    
    setSelectedTeam({
      ...selectedTeam,
      players: updatedPlayers
    });
    
    setEditingPlayer(null);
    setEditPlayerName('');
    setEditPlayerRole('');
  };

  const cancelEditPlayer = () => {
    setEditingPlayer(null);
    setEditPlayerName('');
    setEditPlayerRole('');
  };

  const selectTeamForPlayers = (team) => {
    setSelectedTeam(team);
  };

  // 大会管理機能
  const addTournament = () => {
    if (newTournamentName.trim() === '') return;
    
    const newTournament = {
      id: Date.now(),
      name: newTournamentName.trim(),
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    
    setTournaments([...tournaments, newTournament]);
    setCurrentTournament(newTournament);
    setNewTournamentName('');
    setShowTournamentForm(false);
  };

  const selectTournament = (tournament) => {
    setCurrentTournament(tournament);
  };

  const deleteTournament = (tournamentId) => {
    if (window.confirm('この大会を削除しますか？関連するデータも削除されます。')) {
      setTournaments(tournaments.filter(t => t.id !== tournamentId));
      if (currentTournament && currentTournament.id === tournamentId) {
        setCurrentTournament(null);
      }
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <div className="team-management">
      <div className="section-header">
        <h2>🎮 第五人格プロチーム管理</h2>
        <p>プロチーム、選手、大会を管理します</p>
      </div>

      {/* プロチーム管理セクション（大会非依存） */}
      <div className="team-section">
        <div className="section-divider">
          <h3>👥 プロチーム管理</h3>
          <p className="section-description">プロチームは大会に関係なく登録・管理できます</p>
        </div>

        {/* チーム追加フォーム */}
        <div className="add-team-form">
          <div className="form-group">
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, addTeam)}
              placeholder="プロチーム名を入力してください（例：Team Liquid、NOVA Esports）"
              className="team-input"
            />
            <button 
              onClick={addTeam}
              className="add-button"
              disabled={newTeamName.trim() === ''}
            >
              ➕ チームを追加
            </button>
          </div>
        </div>

        {/* チーム一覧 */}
        <div className="teams-list">
          <div className="list-header">
            <h4>登録チーム一覧 ({teams.length}チーム)</h4>
          </div>

          {teams.length === 0 ? (
            <div className="empty-state">
              <p>まだプロチームが登録されていません</p>
              <p>上のフォームからプロチームを追加してください</p>
            </div>
          ) : (
            <div className="teams-grid">
              {teams.map((team, index) => (
                <div key={team.id} className="team-card">
                  <div className="team-number">#{index + 1}</div>
                  
                  {editingTeam === team.id ? (
                    <div className="edit-form">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, saveEdit)}
                        className="edit-input"
                        autoFocus
                      />
                      <div className="edit-buttons">
                        <button onClick={saveEdit} className="save-button">
                          ✅ 保存
                        </button>
                        <button onClick={cancelEdit} className="cancel-button">
                          ❌ キャンセル
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="team-info">
                      <div className="team-name">{team.name}</div>
                      <div className="team-players-count">
                        選手数: {team.players?.length || 0}名
                      </div>
                      <div className="team-actions">
                        <button 
                          onClick={() => selectTeamForPlayers(team)}
                          className="players-button"
                          title="選手管理"
                        >
                          👤 選手管理
                        </button>
                        <button 
                          onClick={() => startEdit(team)}
                          className="edit-button"
                          title="編集"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={() => deleteTeam(team.id)}
                          className="delete-button"
                          title="削除"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 選手管理セクション */}
      {selectedTeam && (
        <div className="players-section">
          <div className="section-divider">
            <h3>👤 選手管理 - {selectedTeam.name}</h3>
            <button 
              onClick={() => setSelectedTeam(null)}
              className="close-button"
            >
              ❌ 閉じる
            </button>
          </div>

          {/* 選手追加フォーム */}
          <div className="add-player-form">
            <div className="form-group">
              <input
                type="text"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, addPlayer)}
                placeholder="選手名を入力してください"
                className="player-input"
              />
              <select
                value={newPlayerRole}
                onChange={(e) => setNewPlayerRole(e.target.value)}
                className="role-select"
              >
                <option value="サバイバー">サバイバー</option>
                <option value="ハンター">ハンター</option>
                <option value="オールラウンド">オールラウンド</option>
              </select>
              <button 
                onClick={addPlayer}
                className="add-button"
                disabled={newPlayerName.trim() === ''}
              >
                ➕ 選手を追加
              </button>
            </div>
          </div>

          {/* 選手一覧 */}
          <div className="players-list">
            <div className="list-header">
              <h4>所属選手一覧 ({selectedTeam.players?.length || 0}名)</h4>
            </div>

            {!selectedTeam.players || selectedTeam.players.length === 0 ? (
              <div className="empty-state">
                <p>まだ選手が登録されていません</p>
                <p>上のフォームから選手を追加してください</p>
              </div>
            ) : (
              <div className="players-grid">
                {selectedTeam.players.map((player, index) => (
                  <div key={player.id} className="player-card">
                    <div className="player-number">#{index + 1}</div>
                    
                    {editingPlayer === player.id ? (
                      <div className="edit-form">
                        <input
                          type="text"
                          value={editPlayerName}
                          onChange={(e) => setEditPlayerName(e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, saveEditPlayer)}
                          className="edit-input"
                          autoFocus
                        />
                        <select
                          value={editPlayerRole}
                          onChange={(e) => setEditPlayerRole(e.target.value)}
                          className="role-select"
                        >
                          <option value="サバイバー">サバイバー</option>
                          <option value="ハンター">ハンター</option>
                          <option value="オールラウンド">オールラウンド</option>
                        </select>
                        <div className="edit-buttons">
                          <button onClick={saveEditPlayer} className="save-button">
                            ✅ 保存
                          </button>
                          <button onClick={cancelEditPlayer} className="cancel-button">
                            ❌ キャンセル
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="player-info">
                        <div className="player-name">{player.name}</div>
                        <div className={`player-role ${player.role.toLowerCase()}`}>
                          {player.role}
                        </div>
                        <div className="player-actions">
                          <button 
                            onClick={() => startEditPlayer(player)}
                            className="edit-button"
                            title="編集"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => deletePlayer(player.id)}
                            className="delete-button"
                            title="削除"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 大会管理セクション */}
      <div className="tournament-management">
        <div className="tournament-header">
          <h3>🏆 大会管理</h3>
          <button 
            onClick={() => setShowTournamentForm(!showTournamentForm)}
            className="add-button"
          >
            ➕ 新しい大会を作成
          </button>
        </div>

        {showTournamentForm && (
          <div className="add-tournament-form">
            <div className="form-group">
              <input
                type="text"
                value={newTournamentName}
                onChange={(e) => setNewTournamentName(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, addTournament)}
                placeholder="大会名を入力してください（例：第五人格プロリーグ 2024 Spring）"
                className="team-input"
              />
              <button 
                onClick={addTournament}
                className="add-button"
                disabled={newTournamentName.trim() === ''}
              >
                🏆 大会を作成
              </button>
            </div>
          </div>
        )}

        {tournaments.length > 0 && (
          <div className="tournaments-list">
            <h4>登録済み大会 ({tournaments.length}大会)</h4>
            <div className="tournaments-grid">
              {tournaments.map((tournament) => (
                <div 
                  key={tournament.id} 
                  className={`tournament-card ${currentTournament?.id === tournament.id ? 'active' : ''}`}
                >
                  <div className="tournament-info">
                    <div className="tournament-name">{tournament.name}</div>
                    <div className="tournament-date">
                      作成日: {new Date(tournament.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="tournament-actions">
                    <button 
                      onClick={() => selectTournament(tournament)}
                      className={`select-button ${currentTournament?.id === tournament.id ? 'selected' : ''}`}
                    >
                      {currentTournament?.id === tournament.id ? '✅ 選択中' : '📋 選択'}
                    </button>
                    <button 
                      onClick={() => deleteTournament(tournament.id)}
                      className="delete-button"
                      title="削除"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentTournament && (
          <div className="current-tournament-info">
            <p>✅ 現在選択中の大会: <strong>{currentTournament.name}</strong></p>
            <p>この大会で試合記録と統計が管理されます。</p>
          </div>
        )}
      </div>

      {/* 統計情報 */}
      {teams.length > 0 && (
        <div className="team-stats">
          <div className="stats-card">
            <h4>📊 統計情報</h4>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">登録チーム数:</span>
                <span className="stat-value">{teams.length}チーム</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">総選手数:</span>
                <span className="stat-value">
                  {teams.reduce((total, team) => total + (team.players?.length || 0), 0)}名
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">総当たり戦:</span>
                <span className="stat-value">
                  {teams.length >= 2 ? '✅ 実施可能' : '❌ 2チーム以上必要'}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">予想試合数:</span>
                <span className="stat-value">
                  {teams.length >= 2 ? (teams.length * (teams.length - 1)) / 2 : 0}試合
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ゲーム説明 */}
      <div className="game-explanation">
        <div className="explanation-card">
          <h4>🎮 第五人格プロリーグについて</h4>
          <div className="explanation-content">
            <div className="game-format">
              <h5>チーム対戦形式</h5>
              <ul>
                <li>1チーム vs 1チーム の対戦</li>
                <li>各チーム内で5名の選手が対戦（ハンター1名 vs サバイバー4名）</li>
                <li>各試合は前半戦・後半戦の2ラウンド制</li>
                <li>前半戦と後半戦でハンター・サバイバー役が入れ替わる</li>
              </ul>
            </div>
            <div className="scoring-system">
              <h5>得点システム</h5>
              <ul>
                <li>ハンター: 4吊り=5点、3吊り=3点、2吊り=2点、1吊り=1点、0吊り=0点</li>
                <li>サバイバー: 4人逃げ=5点、3人逃げ=3点、2人逃げ=2点、1人逃げ=1点、0人逃げ=0点</li>
                <li>前半戦・後半戦の合計得点で順位決定</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;
